import { useEffect, useMemo, useState, Fragment } from 'react'
import { Download, Gauge, Eye, X, ClipboardList } from 'lucide-react'
import { formatSessionDate, formatSessionTime } from '../../../utils/helpers'
import adminApi from '../../../services/adminApi'
import {
  SECTIONS as SESSION12_SECTIONS,
  FORM_TITLE as SESSION12_FORM_TITLE,
  formatSession12SectionValue,
  OTHERS_OPTION as SESSION12_OTHERS_OPTION,
} from '../../../config/session12DailyRitualsForm'

const COHORT_OPTIONS = [
  { slug: 'energizers-june-cohort', label: 'Energizers June Cohort' },
  { slug: 'energizers-march-cohort', label: 'Energizers March Cohort' },
  { slug: 'build3-cohort-7', label: 'Build3 Cohort 7' },
]

const SCORE_KEYS = [
  { key: 'energyLevel', label: 'Energy', emoji: '⚡', bgClass: 'bg-yellow-500/20', textClass: 'text-yellow-300' },
  { key: 'stressLevel', label: 'Stress', icon: Gauge, bgClass: 'bg-red-500/20', textClass: 'text-red-300' },
  { key: 'emotionalState', label: 'Emotional', emoji: '💜', bgClass: 'bg-purple-500/20', textClass: 'text-purple-300' },
  { key: 'physicalState', label: 'Physical', emoji: '💪', bgClass: 'bg-green-500/20', textClass: 'text-green-300' },
]

const ScoreBadge = ({ scoreKey, value }) => {
  const config = SCORE_KEYS.find(s => s.key === scoreKey)
  if (!config || value == null) return <span className="text-gray-500">-</span>
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bgClass} ${config.textClass}`}>
      {Icon ? <Icon className="w-3 h-3 mr-0.5" /> : <span className="mr-0.5">{config.emoji}</span>}
      {value}
    </span>
  )
}

const SessionDetailModal = ({ session, checkIns, feedback, onClose }) => {
  const sessionCheckIns = checkIns.filter(ci => ci.sessionNumber === session.number)
  const sessionFeedback = feedback.filter(fb => fb.sessionNumber === session.number)

  const userMap = new Map()
  for (const ci of sessionCheckIns) {
    const user = ci.userProgram?.user
    if (!user) continue
    if (!userMap.has(user.id)) {
      userMap.set(user.id, { user, pre: null, post: null, feedback: null })
    }
    if (ci.checkInType === 'pre-event') userMap.get(user.id).pre = ci
    if (ci.checkInType === 'post-event') userMap.get(user.id).post = ci
  }
  for (const fb of sessionFeedback) {
    const user = fb.userProgram?.user
    if (!user) continue
    if (!userMap.has(user.id)) {
      userMap.set(user.id, { user, pre: null, post: null, feedback: null })
    }
    userMap.get(user.id).feedback = fb
  }

  const rows = Array.from(userMap.values()).sort((a, b) =>
    (a.user.name || a.user.email).localeCompare(b.user.name || b.user.email)
  )

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4 pb-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-white/20 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Session {session.number}: {session.title}
            </h3>
            <p className="text-sm text-gray-400">
              {formatSessionDate(session.sessionDateTime)} &middot; {session.facilitator}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-6">
          {rows.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No check-ins or feedback for this session yet.</p>
          ) : (
            <table className="min-w-full divide-y divide-white/10">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Pre-Event</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Post-Event</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Change</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">Feedback Ratings</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Comments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {rows.map(({ user, pre, post, feedback: fb }) => {
                  const diffs = SCORE_KEYS.map(({ key }) => {
                    const preVal = pre?.[key]
                    const postVal = post?.[key]
                    if (preVal == null || postVal == null) return null
                    const diff = postVal - preVal
                    const isStress = key === 'stressLevel'
                    const improved = isStress ? diff < 0 : diff > 0
                    const declined = isStress ? diff > 0 : diff < 0
                    return { key, diff, improved, declined }
                  })

                  return (
                    <tr key={user.id} className="hover:bg-white/5">
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-white">{user.name}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-300">{user.email}</td>
                      <td className="px-3 py-3">
                        {pre ? (
                          <div className="flex flex-wrap justify-center gap-1">
                            {SCORE_KEYS.map(({ key }) => <ScoreBadge key={key} scoreKey={key} value={pre[key]} />)}
                          </div>
                        ) : (
                          <span className="block text-center text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {post ? (
                          <div className="flex flex-wrap justify-center gap-1">
                            {SCORE_KEYS.map(({ key }) => <ScoreBadge key={key} scoreKey={key} value={post[key]} />)}
                          </div>
                        ) : (
                          <span className="block text-center text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {pre && post ? (
                          <div className="flex flex-wrap justify-center gap-1">
                            {diffs.map((d) => {
                              if (!d) return null
                              const colorClass = d.improved
                                ? 'bg-green-500/20 text-green-300'
                                : d.declined
                                ? 'bg-red-500/20 text-red-300'
                                : 'bg-gray-500/20 text-gray-400'
                              return (
                                <span key={d.key} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}>
                                  {d.diff > 0 ? '+' : ''}{d.diff}
                                </span>
                              )
                            })}
                          </div>
                        ) : (
                          <span className="block text-center text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {fb ? (
                          <div className="flex flex-wrap justify-center gap-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-300" title="Enjoyment">
                              E:{fb.enjoymentRating}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-500/20 text-cyan-300" title="Technique">
                              T:{fb.techniqueUsageRating}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-500/20 text-indigo-300" title="Facilitator">
                              F:{fb.facilitatorRating}
                            </span>
                          </div>
                        ) : (
                          <span className="block text-center text-gray-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300 max-w-xs">
                        {fb?.additionalFeedback || <span className="text-gray-500">-</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

const FORM_FIELD_LABELS = {
  primaryHealthGoal: 'Health Goal',
  dietaryPreferences: 'Diet',
  outsideFoodConsumption: 'Outside Food',
  dailyEnergyLevels: 'Energy (1-10)',
  physicalActivity: 'Physical Activity',
  healthCondition: 'Health Condition',
  mealTimingConsistency: 'Meal Timing (1-10)',
  waterIntake: 'Water Intake',
  queries: 'Queries',
}

const formatFormValue = (value) => {
  if (Array.isArray(value)) return value.join(', ')
  if (value == null) return '-'
  return String(value)
}

const FormResponsesModal = ({ session, cohortSlug, onClose }) => {
  const [responses, setResponses] = useState([])
  const [loading, setLoading] = useState(true)
  const isSession12 = session.number === 12

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const res = await adminApi.get(`/api/admin/programs/${cohortSlug}/session-forms/${session.number}`)
        if (res.data.success) setResponses(res.data.data)
      } catch (err) {
        console.error('Failed to fetch form responses:', err)
      }
      setLoading(false)
    }
    fetchResponses()
  }, [cohortSlug, session.number])

  const fieldKeys = Object.keys(FORM_FIELD_LABELS)

  const modalTitle = isSession12
    ? `${SESSION12_FORM_TITLE} — Session ${session.number}: ${session.title}`
    : `Health Form Responses - Session ${session.number}: ${session.title}`

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-6 sm:pt-10 px-3 sm:px-4 pb-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative bg-slate-900 border border-white/20 rounded-2xl shadow-2xl w-full ${
          isSession12 ? 'max-w-3xl' : 'max-w-6xl'
        } max-h-[85vh] overflow-hidden flex flex-col`}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/10">
          <div className="min-w-0 pr-2">
            <h3 className="text-base sm:text-lg font-semibold text-white leading-snug">{modalTitle}</h3>
            <p className="text-sm text-gray-400">{responses.length} response{responses.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-4 sm:p-6">
          {loading ? (
            <p className="text-gray-400 text-center py-8">Loading...</p>
          ) : responses.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No form responses yet.</p>
          ) : isSession12 ? (
            <div className="space-y-6">
              {responses.map((r) => {
                const user = r.userProgram?.user
                const data = r.formData || {}
                return (
                  <div key={r.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                      <div>
                        <p className="text-sm font-medium text-white">{user?.name || '-'}</p>
                        <p className="text-xs text-gray-400">{user?.email || '-'}</p>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(r.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="space-y-3">
                      {SESSION12_SECTIONS.map((section) => {
                        const selected = data[section.id]
                        const othersText = data[`${section.id}_othersText`]
                        const text = formatSession12SectionValue(
                          selected,
                          selected?.includes(SESSION12_OTHERS_OPTION) ? othersText : ''
                        )
                        return (
                          <Fragment key={section.id}>
                            {section.groupTitle && (
                              <div className="pt-2 pb-1 border-b border-white/20 mb-1">
                                <p className="text-sm font-bold text-white">{section.groupTitle}</p>
                              </div>
                            )}
                            <div className="rounded-lg bg-black/20 px-3 py-2">
                              <p className="text-xs font-semibold text-gray-300 mb-1">{section.title}</p>
                              <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">{text}</p>
                            </div>
                          </Fragment>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-white/10">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                  {fieldKeys.map((key) => (
                    <th key={key} className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {FORM_FIELD_LABELS[key]}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {responses.map((r) => {
                  const user = r.userProgram?.user
                  const data = r.formData || {}
                  return (
                    <tr key={r.id} className="hover:bg-white/5">
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-white">{user?.name || '-'}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-300">{user?.email || '-'}</td>
                      {fieldKeys.map((key) => (
                        <td key={key} className="px-3 py-3 text-sm text-gray-300 max-w-[150px] truncate" title={formatFormValue(data[key])}>
                          {formatFormValue(data[key])}
                        </td>
                      ))}
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-300">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

const CohortTab = ({
  cohort,
  cohortStats,
  cohortSessions,
  cohortCheckIns,
  cohortFeedback,
  exportingCSV,
  onExportCSV,
  onExportMarchCSV,
}) => {
  const availableCohorts = useMemo(
    () => COHORT_OPTIONS.filter(({ slug }) => Array.isArray(cohort?.[slug])),
    [cohort]
  )

  const [selectedCohort, setSelectedCohort] = useState(availableCohorts[0]?.slug || COHORT_OPTIONS[0].slug)
  const [subTab, setSubTab] = useState('signups')
  const [detailSession, setDetailSession] = useState(null)
  const [formResponsesSession, setFormResponsesSession] = useState(null)

  useEffect(() => {
    if (!availableCohorts.some(option => option.slug === selectedCohort)) {
      setSelectedCohort(availableCohorts[0]?.slug || COHORT_OPTIONS[0].slug)
    }
  }, [availableCohorts, selectedCohort])

  const selectedOption = COHORT_OPTIONS.find(option => option.slug === selectedCohort) || COHORT_OPTIONS[0]
  const selectedCohortSignups = cohort?.[selectedCohort] || []
  const selectedCohortStats = cohortStats?.[selectedCohort]
  const sessions = cohortSessions?.[selectedCohort] || []
  const checkIns = cohortCheckIns?.[selectedCohort] || []
  const feedbackList = cohortFeedback?.[selectedCohort] || []

  const getCheckInCount = (sessionNumber, type) =>
    checkIns.filter(ci => ci.sessionNumber === sessionNumber && ci.checkInType === type).length

  const getFeedbackCount = (sessionNumber) =>
    feedbackList.filter(fb => fb.sessionNumber === sessionNumber).length

  if (!cohort) return null

  return (
    <div className="space-y-6">
      {/* Cohort Selector */}
      <div className="flex flex-col gap-2 max-w-sm">
        <label htmlFor="cohort-selector" className="text-sm font-medium text-gray-300">
          Select Cohort
        </label>
        <select
          id="cohort-selector"
          value={selectedCohort}
          onChange={(event) => setSelectedCohort(event.target.value)}
          className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-white focus:border-purple-400 focus:outline-none"
        >
          {availableCohorts.map(({ slug, label }) => (
            <option key={slug} value={slug} className="bg-slate-900 text-white">
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Cohort Stats Summary */}
      {selectedCohortStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-4 text-center border border-purple-500/30">
            <div className="text-3xl font-bold text-purple-300">
              {selectedCohortStats.totalSignups}
            </div>
            <div className="text-sm text-gray-400">Total Signups</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-4 text-center border border-yellow-500/30">
            <div className="text-3xl font-bold text-yellow-300">
              ⚡ {selectedCohortStats.averages?.energyLevel?.toFixed(1) || 'N/A'}
            </div>
            <div className="text-sm text-gray-400">Avg Energy</div>
          </div>
          <div className="bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-xl p-4 text-center border border-red-500/30">
            <div className="text-3xl font-bold text-pink-300 flex items-center justify-center gap-2">
              <Gauge className="w-8 h-8" /> {selectedCohortStats.averages?.stressLevel?.toFixed(1) || 'N/A'}
            </div>
            <div className="text-sm text-gray-400">Avg Stress</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-4 text-center border border-purple-500/30">
            <div className="text-3xl font-bold text-indigo-300">
              💜 {selectedCohortStats.averages?.emotionalState?.toFixed(1) || 'N/A'}
            </div>
            <div className="text-sm text-gray-400">Avg Emotional</div>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-teal-500/20 rounded-xl p-4 text-center border border-green-500/30">
            <div className="text-3xl font-bold text-teal-300">
              💪 {selectedCohortStats.averages?.physicalState?.toFixed(1) || 'N/A'}
            </div>
            <div className="text-sm text-gray-400">Avg Physical</div>
          </div>
        </div>
      )}

      {/* Sub-tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-0">
        {[
          { key: 'signups', label: `Signups (${selectedCohortSignups.length})` },
          { key: 'sessions', label: `Sessions & Check-ins (${sessions.length})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              subTab === tab.key
                ? 'bg-white/10 text-white border-b-2 border-purple-400'
                : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Signups Sub-tab */}
      {subTab === 'signups' && (
        <div className="overflow-x-auto">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
            <h3 className="text-xl font-semibold text-white">
              {selectedOption.label} Signups ({selectedCohortSignups.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={onExportCSV}
                disabled={exportingCSV}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                {exportingCSV ? 'Exporting...' : 'Export Build3 CSV'}
              </button>
              <button
                onClick={onExportMarchCSV}
                disabled={exportingCSV}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download className="w-4 h-4" />
                {exportingCSV ? 'Exporting...' : 'Export March Cohort CSV'}
              </button>
            </div>
          </div>
          <table className="min-w-full divide-y divide-white/10">
            <thead>
              <tr>
                {['Name', 'Email', 'Phone', 'Gender', 'Experience', 'Wellness Scores', 'Goal', 'Timing', 'Date'].map(header => (
                  <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {selectedCohortSignups.map((signup) => (
                <tr key={signup.id} className="hover:bg-white/5">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">
                    {signup.name}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {signup.email}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {signup.phone}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">
                    {signup.gender?.replace(/-/g, ' ')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {signup.yogaExperience}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-300">
                    <div className="flex flex-wrap gap-1">
                      {SCORE_KEYS.map(({ key }) => (
                        <ScoreBadge key={key} scoreKey={key} value={signup[key]} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300 capitalize">
                    {signup.wellnessGoal?.replace(/-/g, ' ')}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {signup.timingPreference === 'other'
                      ? signup.customTiming
                      : signup.timingPreference || 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {new Date(signup.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sessions & Check-ins Sub-tab */}
      {subTab === 'sessions' && (
        <div className="overflow-x-auto">
          <h3 className="text-xl font-semibold text-white mb-4">
            {selectedOption.label} Sessions
          </h3>
          {sessions.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No sessions found for this cohort.</p>
          ) : (
            <table className="min-w-full divide-y divide-white/10">
              <thead>
                <tr>
                  {['#', 'Title', 'Date & Time', 'Facilitator', 'Pre Check-ins', 'Post Check-ins', 'Feedback', ''].map(header => (
                    <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {sessions.map((session) => {
                  const preCount = getCheckInCount(session.number, 'pre-event')
                  const postCount = getCheckInCount(session.number, 'post-event')
                  const fbCount = getFeedbackCount(session.number)

                  return (
                    <tr key={session.number} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-purple-300">
                        {session.number}
                      </td>
                      <td className="px-4 py-4 text-sm text-white">
                        <div className="font-medium">{session.title}</div>
                        {session.subtitle && (
                          <div className="text-xs text-gray-400 mt-0.5 italic">{session.subtitle}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        <div>{formatSessionDate(session.sessionDateTime)}</div>
                        <div className="text-xs text-gray-400">{formatSessionTime(session.sessionDateTime, session.endDateTime)}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {session.facilitator}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-sm font-semibold ${
                          preCount > 0 ? 'bg-blue-500/20 text-blue-300' : 'bg-white/5 text-gray-500'
                        }`}>
                          {preCount}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-sm font-semibold ${
                          postCount > 0 ? 'bg-purple-500/20 text-purple-300' : 'bg-white/5 text-gray-500'
                        }`}>
                          {postCount}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full text-sm font-semibold ${
                          fbCount > 0 ? 'bg-teal-500/20 text-teal-300' : 'bg-white/5 text-gray-500'
                        }`}>
                          {fbCount}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setDetailSession(session)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-300 hover:text-white bg-purple-500/10 hover:bg-purple-500/20 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Details
                          </button>
                          {selectedCohort === 'energizers-march-cohort' &&
                            (session.number === 4 || session.number === 12) && (
                            <button
                              onClick={() => setFormResponsesSession(session)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-teal-300 hover:text-white bg-teal-500/10 hover:bg-teal-500/20 rounded-lg transition-colors"
                            >
                              <ClipboardList className="w-4 h-4" />
                              Forms
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Session Detail Modal */}
      {detailSession && (
        <SessionDetailModal
          session={detailSession}
          checkIns={checkIns}
          feedback={feedbackList}
          onClose={() => setDetailSession(null)}
        />
      )}

      {/* Form Responses Modal */}
      {formResponsesSession && (
        <FormResponsesModal
          session={formResponsesSession}
          cohortSlug={selectedCohort}
          onClose={() => setFormResponsesSession(null)}
        />
      )}
    </div>
  )
}

export default CohortTab
